import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import noImage from '../../Resources/Images/no_image.svg';
import getImageUrl from '@configuratorware/configurator-frontendgui/App/Utils/getImageUrl';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { getGroupedComponents } from '../../Reducers/Configurator/Selectors';
import { grey, liightergrey } from '../../Constants/Colors';
import clsx from 'clsx';
import { Edit } from '@material-ui/icons';
import { setStepState } from '../../Reducers/Configurator/Actions';
import { showProductParts } from '../../Reducers/UI/Actions';
import { DETAILS_ID } from '../../Screens/Layout';
import { hidePrices, scrollTo } from '../../Helpers';
import PriceInfoDialog from '../PriceInfoDialog/PriceInfoDialog';

const useStyles = makeStyles(
  (theme) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
    },
    titleLabel: {
      padding: '60px 0',
    },
    iconWrapper: {
      position: 'relative',
      top: '6px',
    },
    groupLabel: {
      fontSize: '18px',
    },
    componentLabel: {
      fontFamily: 'Inter',
      fontWeight: 300,
      fontSize: '17px',
    },
    optionLabel: {
      fontFamily: 'Inter',
      fontWeight: 500,
      fontSize: '15px',
      paddingTop: '22px',
      paddingBottom: '26px',
    },
    price: {
      fontFamily: 'Inter',
      fontWeight: 500,
      fontSize: '15px',
      paddingTop: '22px',
      paddingBottom: '26px',
      cursor: 'pointer',
    },
    accordionDetails: {
      padding: 0,
    },
    accordion: {
      '&:before': {
        display: 'none',
      },
      border: 'none',
      padding: 0,
    },
    accordionSummary: {
      borderBottom: 'solid 1px ' + grey,
      minHeight: '0 !important',
      padding: 0,
      '& .MuiAccordionSummary-content': {
        margin: 0,
      },
      '& .MuiIconButton-root': {
        padding: 0,
      },
    },
    accordionContent: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
    },
    componentContainer: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: '30px',
      padding: '30px',
      backgroundColor: liightergrey,
    },
    optionContainer: {
      display: 'flex',
      flexDirection: 'column',
    },
    optionTitlePrice: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    iconExpand: {
      fontSize: '2.5rem',
      color: 'black',
    },
    optionImage: {
      width: 'max-content',
      height: 150,
      '&.imageNotAvailableElement': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      '& img': {
        height: 60,
        width: 60,
      },
    },
  }),
  {
    name: 'ConfigurationOverviewAccordion',
  }
);

const ConfigurationOverviewAccordion = () => {
  const classes = useStyles();
  const groupedComponents = useSelector(getGroupedComponents);
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const dispatch = useDispatch();
  const pricesHidden = hidePrices();
  const [showPriceInfo, setShowPriceInfo] = useState(false);
  const onPriceClick = () => {
    setShowPriceInfo(true);
  };

  const onCancelPriceInfo = () => {
    setShowPriceInfo(false);
  };

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const goToStep = (event: React.MouseEvent<HTMLSpanElement>, step: string) => {
    event.stopPropagation();
    dispatch(setStepState(step, 'active'));
    dispatch(showProductParts());
    scrollTo(DETAILS_ID);
  };

  return (
    <div className={classes.root}>
      <PriceInfoDialog onCancel={onCancelPriceInfo} open={showPriceInfo} />
      <Typography variant="h2" className={classes.titleLabel}>
        {t('overview.summary')}
      </Typography>
      {groupedComponents.map((groupedComponent, i) => (
        <Accordion
          className={classes.accordion}
          elevation={0}
          onChange={handleChange(groupedComponent.identifier)}
          expanded={groupedComponent.identifier === expanded || (!expanded && i === 0)}
          key={i}
        >
          <AccordionSummary
            className={classes.accordionSummary}
            expandIcon={<ExpandMoreIcon className={classes.iconExpand} />}
          >
            <Typography variant={'h5'}>
              {groupedComponent.title}{' '}
              <span
                className={classes.iconWrapper}
                onClick={(event) => goToStep(event, groupedComponent.identifier)}
              >
                <Edit />
              </span>
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            <div className={classes.accordionContent}>
              {groupedComponent.components.map((component, j) => (
                <div className={classes.componentContainer} key={j}>
                  <Typography className={classes.componentLabel}>{component.title}</Typography>
                  {component.selectedoptions &&
                    component.selectedoptions.length > 0 &&
                    component.selectedoptions.map((option, k) => (
                      <div className={classes.optionContainer} key={k}>
                        <div className={classes.optionTitlePrice}>
                          <Typography className={classes.optionLabel}>{option.title}</Typography>
                          {!pricesHidden && (
                            <Typography onClick={onPriceClick} className={classes.price}>
                              {option.priceFormatted + '*'}
                            </Typography>
                          )}
                        </div>
                        <OptionImage src={getImageUrl(option.thumbnail)} />
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};

interface OptionImageProps {
  src: string;
}

const OptionImage: React.FC<OptionImageProps> = ({ src, ...props }) => {
  const classes = useStyles();
  const [imageAvailable, setImageAvailable] = useState(true);
  return imageAvailable ? (
    <img src={src} className={classes.optionImage} onError={() => setImageAvailable(!imageAvailable)} />
  ) : (
    <div className={clsx('imageNotAvailableElement', classes.optionImage)}>
      <img src={noImage} />
    </div>
  );
};

export default ConfigurationOverviewAccordion;
